/**
 * <bigens-video-strip> (T10): muted UGC clips that loop only while they're on screen.
 *
 * - A clip plays when at least 60 % of it is visible and pauses when it leaves (the strip scrolls sideways on mobile,
 *   so this also covers clips scrolled out of the row).
 * - Every clip gets a pause/play button (WCAG 2.2.2). A clip the shopper paused stays paused until they press play.
 * - prefers-reduced-motion: nothing starts by itself; the poster shows with a play button.
 * - Without this script the clips keep their native controls (rendered in Liquid, removed here).
 * - T41b: the strip is re-rendered by <bigens-stage-sync> when the picker changes family/size, so new clips are
 *   wired up through a MutationObserver (each clip once).
 * Labels come from data-play-label / data-pause-label (translated in Liquid), so there's no copy in JS.
 */
class BigensVideoStrip extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {MutationObserver | null} T41b: the strip re-renders when the picker changes size (bigens-stage-sync) */
  #mutations = null;
  /** @type {WeakSet<HTMLVideoElement>} */
  #bound = new WeakSet();
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#observer = new IntersectionObserver(this.#onIntersect, { threshold: [0, 0.6] });
    this.#bindAll();
    this.#mutations = new MutationObserver(() => this.#bindAll());
    this.#mutations.observe(this, { childList: true, subtree: true });
    this.#reducedMotion.addEventListener('change', this.#onMotionChange);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#mutations?.disconnect();
    this.#mutations = null;
    this.#reducedMotion.removeEventListener('change', this.#onMotionChange);
  }

  /**
   * Wires every clip once (new clips arrive when the strip follows the variant picker). The observer is new on every
   * connect, so each clip is (re-)observed on every pass: a strip moved by a re-render keeps playing in view (T44).
   */
  #bindAll() {
    for (const video of this.#videos()) {
      this.#observer?.observe(video);
      if (this.#bound.has(video)) continue;
      this.#bound.add(video);
      video.removeAttribute('controls');
      video.muted = true;
      const button = this.#buttonFor(video);
      if (button) {
        button.hidden = false;
        button.addEventListener('click', () => this.#toggle(video));
      }
      video.addEventListener('play', () => this.#sync(video));
      video.addEventListener('pause', () => this.#sync(video));
      this.#sync(video);
    }
  }

  /** @returns {HTMLVideoElement[]} */
  #videos() {
    return Array.from(this.querySelectorAll('video'));
  }

  /** @param {HTMLVideoElement} video */
  #buttonFor(video) {
    return video.parentElement?.querySelector('.bigens-pdp-videos__toggle') ?? null;
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    for (const entry of entries) {
      const video = /** @type {HTMLVideoElement} */ (entry.target);
      video.dataset.inView = String(entry.intersectionRatio >= 0.6);
      if (entry.intersectionRatio >= 0.6) {
        if (!this.#reducedMotion.matches && video.dataset.userPaused !== 'true') this.#play(video);
      } else if (!video.paused) {
        video.pause();
      }
    }
  };

  #onMotionChange = () => {
    if (!this.#reducedMotion.matches) return;
    for (const video of this.#videos()) {
      if (video.dataset.userStarted !== 'true') video.pause();
    }
  };

  /** @param {HTMLVideoElement} video */
  #toggle(video) {
    if (video.paused) {
      video.dataset.userPaused = 'false';
      video.dataset.userStarted = 'true';
      this.#play(video);
    } else {
      video.dataset.userPaused = 'true';
      video.pause();
    }
  }

  /** @param {HTMLVideoElement} video */
  #play(video) {
    if (video.preload === 'none') video.preload = 'auto';
    video.play().catch(() => this.#sync(video));
  }

  /** @param {HTMLVideoElement} video */
  #sync(video) {
    const button = this.#buttonFor(video);
    if (!button) return;
    const playing = !video.paused;
    button.dataset.state = playing ? 'playing' : 'paused';
    button.setAttribute('aria-label', (playing ? this.dataset.pauseLabel : this.dataset.playLabel) ?? '');
  }
}

if (!customElements.get('bigens-video-strip')) {
  customElements.define('bigens-video-strip', BigensVideoStrip);
}
